/**
 * XMLUI Footer Component
 * 
 * This component provides the application footer with:
 * - About MoneyMojo information
 * - Support links
 * - Copyright and legal information
 * 
 * Features:
 * - Accessibility compliance (WCAG 2.2 AA)
 * - Responsive design
 * - Semantic footer structure
 * 
 * Note: Compliance section is currently commented out
 */

import React from 'react';
import { Heart } from 'lucide-react';

/**
 * Footer Component
 * 
 * Provides the application footer with important links and information.
 */
export function Footer(): React.ReactElement {
  const currentYear = new Date().getFullYear();

  return (
    <footer 
      className="border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="w-full max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-3">
          {/* About MoneyMojo */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              About MoneyMojo
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Australian-first personal finance management application with AI-powered 
              categorisation and comprehensive financial insights.
            </p>
            <div className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400">
              <span>Made with</span>
              <Heart className="h-4 w-4 text-red-500" />
              <span>in Australia</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Quick Links
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="/transactions"
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus-ring rounded"
                >
                  Add Transaction
                </a>
              </li>
              <li>
                <a
                  href="/budgets"
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus-ring rounded"
                >
                  Create Budget
                </a>
              </li>
              <li>
                <a
                  href="/budgets?tab=savings"
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus-ring rounded"
                >
                  Set Savings Goal
                </a>
              </li>
              <li>
                <a
                  href="/settings"
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus-ring rounded"
                >
                  Account Settings
                </a>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Support
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="/help"
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus-ring rounded"
                >
                  Help Centre
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus-ring rounded"
                >
                  About Us
                </a>
              </li>
              <li>
                <a
                  href="/privacy-policy"
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus-ring rounded"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="/terms"
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus-ring rounded"
                >
                  Terms of Service
                </a>
              </li>
            </ul>
          </div>

          {/* Compliance */}
          {/* <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Compliance
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <span className="text-gray-600 dark:text-gray-400">
                  WCAG 2.2 AA Compliant
                </span>
              </li>
              <li>
                <span className="text-gray-600 dark:text-gray-400">
                  Australian Privacy Act 1988
                </span>
              </li>
              <li>
                <span className="text-gray-600 dark:text-gray-400">
                  ATO Standards Aligned
                </span>
              </li>
              <li>
                <span className="text-gray-600 dark:text-gray-400">
                  Bank-level Security
                </span>
              </li>
            </ul>
          </div> */}
        </div>

        {/* Bottom section */}
        <div className="mt-8 border-t border-gray-200 pt-8 dark:border-gray-700">
          <div className="flex flex-col items-center justify-between space-y-4 md:flex-row md:space-y-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              © {currentYear} MoneyMojo. All rights reserved.
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
              <span>Version 1.0.0</span>
              <span>•</span>
              <div className="flex items-center space-x-2">
                <span>Powered by</span>
                <img 
                  src="/mindmacros_logo.svg" 
                  alt="Mindmacros" 
                  className="h-4 w-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
